'use client'

import * as React from 'react'
import { useAuth } from '@clerk/nextjs'
import { isAuthError } from '@/lib/authError'

/**
 * Last line of defense against the Clerk/Convex sign-out race.
 *
 * When the Convex auth token drops (sign-out, or expiry after the tab was
 * asleep), the server re-executes all subscribed queries unauthenticated and
 * pushes "Authentication required" errors into still-mounted useQuery hooks,
 * which throw during render, possibly BEFORE useConvexAuth().isAuthenticated
 * flips, so no amount of `'skip'` gating fully prevents it. Ungated page-level
 * queries (dashboard charts etc.) are exposed regardless.
 *
 * This boundary sits directly inside ConvexProviderWithClerk, above every
 * component that can call useQuery (LocaleProvider, ConditionalLayout, pages),
 * and catches ONLY auth errors (isAuthError). Recovery:
 *  - signed out (the normal logout flow): hard-redirect to /sign-in
 *  - still signed in (transient token hiccup): remount the subtree a few
 *    times with backoff so queries re-subscribe with a fresh token, then
 *    fall back to a full reload
 * Anything else is re-thrown so global-error keeps handling real bugs.
 */

const MAX_RETRIES = 3

// How long a remount attempt must stay error-free before we consider the
// recovery successful and restore the full retry budget.
const RECOVERY_RESET_MS = 30_000

// sessionStorage circuit breaker: if the reload fallback fired less than this
// long ago, a persistent auth error is looping us through reloads. Stop and
// show a manual-recovery screen instead.
const RELOAD_LOOP_KEY = 'authErrorReloadedAt'
const RELOAD_LOOP_WINDOW_MS = 60_000

function FullScreenSpinner() {
  return (
    <div
      className="flex items-center justify-center min-h-screen"
      style={{ background: 'var(--color-background)' }}
    >
      <div
        className="animate-spin rounded-full h-12 w-12 border-b-2"
        style={{ borderColor: 'var(--color-lime)' }}
      ></div>
    </div>
  )
}

interface InnerProps {
  children: React.ReactNode
  onAuthError: () => void
}

interface InnerState {
  error: Error | null
}

class AuthErrorBoundaryInner extends React.Component<InnerProps, InnerState> {
  state: InnerState = { error: null }

  static getDerivedStateFromError(error: Error): InnerState {
    return { error }
  }

  componentDidCatch(error: Error) {
    if (isAuthError(error)) {
      console.warn('[AuthErrorBoundary] Caught auth error during auth transition:', error)
      this.props.onAuthError()
    }
  }

  render() {
    const { error } = this.state
    if (error) {
      // Not an auth race, let global-error (or an outer boundary) handle it.
      if (!isAuthError(error)) throw error
      return <FullScreenSpinner />
    }
    return this.props.children
  }
}

// NOTE: rendered above LocaleProvider/NextIntlClientProvider, so no
// useTranslations here; and rendering ErrorState is unsafe (it runs a Convex
// query that could rethrow the very auth error we are recovering from). Keep
// this hardcoded, same rationale as UserInitializer and global-error.
function ReloadLoopFallback() {
  const handleRetry = () => {
    try {
      sessionStorage.removeItem(RELOAD_LOOP_KEY)
    } catch {
      // ignore
    }
    window.location.reload()
  }
  return (
    <div
      className="flex items-center justify-center min-h-screen"
      style={{ background: 'var(--color-background)' }}
    >
      <div className="text-center max-w-md p-6">
        <h1 className="text-xl font-semibold" style={{ color: 'var(--color-text)' }}>
          Having trouble signing you in
        </h1>
        <p className="mt-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          We could not restore your session. Try again, or sign in from scratch.
        </p>
        <div className="mt-6 flex gap-3 justify-center">
          <button
            onClick={handleRetry}
            className="px-4 py-2 rounded-lg font-semibold"
            style={{ background: 'var(--color-lime)', color: '#0b0b0c' }}
          >
            Try again
          </button>
          <button
            onClick={() => window.location.replace('/sign-in')}
            className="px-4 py-2 rounded-lg border"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text)' }}
          >
            Go to sign in
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AuthErrorBoundary({
  children,
}: {
  children: React.ReactNode
}) {
  const { isLoaded, isSignedIn } = useAuth()
  // Bumped on every caught auth error; drives the recovery effect below.
  const [errorEpoch, setErrorEpoch] = React.useState(0)
  // Remount key for the inner boundary, bumping it clears the error state
  // and re-renders children, re-subscribing their queries with fresh auth.
  const [resetKey, setResetKey] = React.useState(0)
  // Reload-loop circuit breaker tripped: stop recovering automatically.
  const [gaveUp, setGaveUp] = React.useState(false)
  const retries = React.useRef(0)
  const errorEpochRef = React.useRef(0)

  const handleAuthError = React.useCallback(() => {
    setErrorEpoch((e) => e + 1)
  }, [])

  React.useEffect(() => {
    errorEpochRef.current = errorEpoch
  }, [errorEpoch])

  // After a remount attempt, if no new auth error arrives for a while the
  // recovery worked: restore the full retry budget for the next, unrelated
  // auth transition (review nit: retries otherwise never reset per page life).
  React.useEffect(() => {
    if (resetKey === 0) return
    const epochAtReset = errorEpochRef.current
    const timer = setTimeout(() => {
      if (errorEpochRef.current === epochAtReset) {
        retries.current = 0
      }
    }, RECOVERY_RESET_MS)
    return () => clearTimeout(timer)
  }, [resetKey])

  React.useEffect(() => {
    if (errorEpoch === 0) return

    // Signed out: this is the normal logout flow. Hard-navigate to /sign-in
    // (Clerk's afterSignOutUrl redirect may also land, same destination).
    if (isLoaded && !isSignedIn) {
      window.location.replace('/sign-in')
      return
    }

    // Clerk still reports signed in (or is still loading): transient token
    // failure, or Clerk's state hasn't flipped yet. Retry with backoff -
    // if the user is mid-sign-out, isSignedIn flips false shortly and the
    // branch above redirects instead.
    if (retries.current < MAX_RETRIES) {
      const timer = setTimeout(
        () => {
          retries.current += 1
          setResetKey((k) => k + 1)
        },
        1000 * (retries.current + 1),
      )
      return () => clearTimeout(timer)
    }

    // Retries exhausted while still signed in, a full reload re-bootstraps
    // Clerk + Convex from scratch (middleware redirects if truly signed out).
    // Circuit breaker: retries.current resets on every page load, so a
    // PERSISTENT auth error would otherwise loop retries -> reload forever.
    // If we already reloaded for this recently, give up and let the user act.
    let lastReloadAt = 0
    try {
      lastReloadAt = Number(sessionStorage.getItem(RELOAD_LOOP_KEY)) || 0
    } catch {
      // sessionStorage unavailable: still reload, the loop just isn't tracked
    }
    if (Date.now() - lastReloadAt < RELOAD_LOOP_WINDOW_MS) {
      setGaveUp(true)
      return
    }
    try {
      sessionStorage.setItem(RELOAD_LOOP_KEY, String(Date.now()))
    } catch {
      // ignore
    }
    window.location.reload()
  }, [errorEpoch, isLoaded, isSignedIn])

  if (gaveUp) {
    return <ReloadLoopFallback />
  }

  return (
    <AuthErrorBoundaryInner key={resetKey} onAuthError={handleAuthError}>
      {children}
    </AuthErrorBoundaryInner>
  )
}
